import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import {Loader2, Save, X } from "lucide-react";
import Button from "../../../shared/components/button";
import { useAccountManager } from "../hooks/useAccountManager";
import { useAuth } from "../../login/hooks/useAuth";
import type CreateUser from "../models/CreateUser";
import Roles from "@/shared/valueObjects/Roles";
import Gender from "@/shared/valueObjects/Gender";
import Field from "@/shared/components/field";
import GlassBackground from "@/shared/components/glassBackground";
import BackButton from "@/shared/components/backButton";

export default function NewAccountPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { createAccount, updateAccount, getAccount, loading, error } =
    useAccountManager();

  const [formData, setFormData] = useState<CreateUser>({
    name: "",
    email: "",
    role: 0,
    gender: Gender.unespecified,
    phoneNumber: "",
    city: "",
    country: "",
    birthday: (() => {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 18);
      return d.toISOString().split("T")[0];
    })(),
  });

  const isEditing = !!id;

  const availableRoles = useMemo(() => {
    const roles: { value: number; label: string }[] = [];
    if (Roles.isSuperAdmin(currentUser?.role)) {
      roles.push({ value: 1, label: "Administrador" });
      roles.push({ value: 0, label: "Cuenta Estándar" });
    } else if (Roles.isAdmin(currentUser?.role)) {
      roles.push({ value: 0, label: "Cuenta Estándar" });
    }
    return roles;
  }, [currentUser]);

  useEffect(() => {
    if (!isEditing) return;
    let ignore = false;
    const fetchAccount = async () => {
      const account = await getAccount(id);
      if (!ignore && account) {
        setFormData({
          name: account.name,
          email: account.email,
          role: account.role as any,
          gender: account.gender ?? Gender.unespecified,
          phoneNumber: account.phoneNumber,
          city: account.city,
          country: account.country,
          birthday: account.birthday,
        });
      }
    };
    fetchAccount();
    return () => { ignore = true; };
  }, [id, isEditing, getAccount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing) {
      await updateAccount(id, formData);
    } else {
      await createAccount(formData);
    }
  };

  useEffect(() => {
    if (!isEditing && availableRoles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        role: availableRoles[0].value as any,
      }));
    }
  }, [availableRoles, isEditing]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    if (type === "date") {
      setFormData((prev) => ({ ...prev, [name]: value || null }));
      return;
    }
    const isNumeric = name === "role" || name === "gender";
    const finalValue = isNumeric ? Number(value) : value;

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  return (
    <GlassBackground>
      <div className="relative flex-1 overflow-auto p-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <BackButton text={"Volver a cuentas"} link={"/admin/accounts"}>
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              {isEditing ? "Editar Cuenta" : "Nueva Cuenta"}
            </h2>
            <p className="text-gray text-lg font-medium mt-1">
              {isEditing
                ? "Actualiza la información de la sub-cuenta."
                : "Configura una nueva sub-cuenta para tu organización."}
            </p>
          </BackButton>

          {/* Form Card */}
          <div className="bg-white/60 border border-black/5 backdrop-blur-md rounded-4xl p-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)]">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Field
                label={"Nombre Completo"}
                name={"name"}
                value={formData.name}
                placeholder="Ej: Juan Pérez o TechCorp"
                required
                onChange={handleChange}
              />

              <Field
                label={"Correo Electrónico"}
                name={"email"}
                value={formData.email}
                placeholder="juan@empresa.com"
                required
                onChange={handleChange}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label={"Teléfono"}
                  name={"phoneNumber"}
                  value={formData.phoneNumber}
                  placeholder="+593 09 123 4567"
                  onChange={handleChange}
                />
                <Field
                  label={" Fecha de Nacimiento"}
                  name={"birthday"}
                  value={formData.birthday}
                  type="date"
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label={"Ciudad"}
                  name={"city"}
                  value={formData.city || ""}
                  onChange={handleChange}
                  placeholder="Ej: Cuenca"
                />

                <Field
                  label={"País"}
                  name={"country"}
                  value={formData.country || ""}
                  onChange={handleChange}
                  placeholder="Ej: Ecuador"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Rol de Usuario"
                  name="role"
                  type="select"
                  value={formData.role}
                  onChange={handleChange}
                  options={availableRoles}
                  disabled={availableRoles.length === 1}
                />

                <Field
                  label="Género"
                  name="gender"
                  type="select"
                  value={formData.gender}
                  onChange={handleChange}
                  options={[
                    { value: Gender.unespecified, label: "No especificado" },
                    { value: Gender.male, label: "Masculino" },
                    { value: Gender.female, label: "Femenino" },
                  ]}
                />
              </div>

              {error && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 text-sm font-medium">
                  {error.message}
                </div>
              )}

              <div className="pt-4 flex items-center gap-4">
                <Button submit disabled={loading}>
                  {loading ? (
                    <Loader2 className="size-5 animate-spin mx-auto" />
                  ) : (
                    <>
                      <Save className="size-5" />
                      <span className="font-bold ml-2">
                        {isEditing ? "Guardar Cambios" : "Crear Sub-cuenta"}
                      </span>
                    </>
                  )}
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => navigate("/admin/accounts")}
                >
                  <X className="size-5" />
                  <span className="font-bold">Cancelar</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </GlassBackground>
  );
}
